import { SCENE_WIDTH, SCENE_HEIGHT } from "./templateGeometry.js";
import { defineDesignedMap } from "./maps/_helpers.js";

// Player-authored custom levels, persisted in localStorage.
//
// A custom level is the WYSIWYG editor's source of truth: ONE continuous,
// 20-cell-tall grid that the designer paints across, `widthScenes` chunks of 30
// columns wide. The designer never thinks about "scenes" — those are an internal
// streaming unit. We slice the continuous grid into 30-wide scenes only when
// building the runtime map (`buildRuntimeMap`), and tag it for ABSOLUTE
// placement so the game renders each slice exactly where it was painted (dy=0),
// reproducing the canvas pit-for-pit and cliff-for-cliff. See the editor in
// src/editor/ and the "Maps" section of docs/level-generation.md.
//
// StoredMap shape:
//   {
//     id:          "custom-<ts>-<rand>"  // namespaced; never collides with built-ins
//     name:        string
//     description: string
//     updatedAt:   number                // ms epoch, for "most recent" ordering
//     widthScenes: number                // course length in 30-col chunks (>= 1)
//     rows:        string[]              // up to SCENE_HEIGHT rows, each widthScenes*30
//                                        //   wide, top-to-bottom (row 0 = canvas top)
//     slug:        string                // OPTIONAL public share id (per-user unique;
//                                        //   assigned on first cloud save). See
//                                        //   levelSync.js + shareLinks below.
//   }

// Where the player materializes when a custom level is played. The runtime
// always starts the player at world x=0 — the START (left edge) of the painted
// chunk 1 — standing on the floor (see buildRuntimeMap's flat_ground lead-in).
// In the editor's bottom-anchored grid that fixed world position is column 0,
// the third row up from the bottom: the floor fills the bottom two rows and the
// player's 0.5-tall body occupies exactly the cell directly above it. The editor
// marks this cell and locks it so a block/hazard can't be painted where the
// player would spawn (an instant, unavoidable death on frame 1).
export const SPAWN_COL = 0;
export const SPAWN_ROW = SCENE_HEIGHT - 3;

export const CUSTOM_PREFIX = "custom-";
const STORAGE_KEY = "sbbvibe_custom_maps";
const STORE_VERSION = 1;

/** Is this map id one of ours (a player-authored custom level)? */
export function isCustomMapId(id) {
  return typeof id === "string" && id.startsWith(CUSTOM_PREFIX);
}

/**
 * Is this map id a COMMUNITY level — someone else's shared level, addressed as
 * "<username>/<slug>"? Built-in and "custom-" ids never contain "/", so the slash
 * is a safe discriminator. These are fetched from the cloud and registered into
 * the runtime at load time (see levelSync/getPublicLevel + maps/index.js), not
 * stored in localStorage.
 */
export function isCommunityMapId(id) {
  return typeof id === "string" && id.includes("/");
}

/**
 * Maps that have NO shared leaderboard: the player's own local custom levels and
 * other players' community levels (their ids are meaningless to the global board).
 * Used to gate score submission + the leaderboard/map-picker UI.
 */
export function isNonLeaderboardMapId(id) {
  return isCustomMapId(id) || isCommunityMapId(id);
}

// --- localStorage access (always guarded; private mode / quota must not throw) -

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: STORE_VERSION, maps: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || typeof parsed.maps !== "object") {
      return { version: STORE_VERSION, maps: {} };
    }
    return { version: STORE_VERSION, maps: parsed.maps };
  } catch (err) {
    console.warn("customMaps: could not read store; treating as empty.", err);
    return { version: STORE_VERSION, maps: {} };
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    return true;
  } catch (err) {
    console.warn("customMaps: could not persist store (quota/private mode?).", err);
    return false;
  }
}

// --- Mutation observers -------------------------------------------------------
//
// A minimal hook so an optional sync layer (src/levels/levelSync.js) can mirror
// saves/deletes to the cloud WITHOUT this module knowing about Supabase or
// becoming async. Listeners fire after the localStorage write; their return
// values are ignored and a throwing listener can't break a save/delete.

const mutateListeners = [];

/**
 * Subscribe to custom-map mutations. `cb(kind, payload)` where kind is
 * "save" (payload = the saved StoredMap) or "delete" (payload = the id).
 * @returns {() => void} unsubscribe
 */
export function onCustomMapsMutate(cb) {
  mutateListeners.push(cb);
  return () => {
    const i = mutateListeners.indexOf(cb);
    if (i >= 0) mutateListeners.splice(i, 1);
  };
}

function emitMutate(kind, payload) {
  for (const cb of mutateListeners) {
    try {
      cb(kind, payload);
    } catch (err) {
      console.warn("customMaps: a mutate listener threw; ignoring.", err);
    }
  }
}

/**
 * Replace the entire local store with the given maps (an id→StoredMap object or
 * an array of StoredMaps). Used by the sync layer to swap the cache to a signed-in
 * account's cloud set, or clear it on sign-out. Returns false if storage refused
 * the write (quota/private mode) — never throws. Does NOT emit mutations (the
 * sync layer is the source of this change and must not echo it back to the cloud).
 */
export function replaceAllLocal(maps) {
  const byId = Array.isArray(maps)
    ? Object.fromEntries(maps.filter((m) => m && m.id).map((m) => [m.id, m]))
    : maps && typeof maps === "object"
    ? maps
    : {};
  return writeStore({ version: STORE_VERSION, maps: byId });
}

// --- CRUD ---------------------------------------------------------------------

/** All stored custom maps, most-recently-updated first. */
export function listCustomMaps() {
  const { maps } = readStore();
  return Object.values(maps).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/** A single stored custom map by id, or null. */
export function getCustomMap(id) {
  if (!isCustomMapId(id)) return null;
  return readStore().maps[id] || null;
}

/**
 * Persist a stored map (bumps updatedAt). No validation — the editor may hold a
 * transient-invalid grid mid-edit; validation happens at Play time. Returns the
 * saved object (with the refreshed updatedAt) so callers stay in sync.
 */
export function saveCustomMap(stored) {
  const store = readStore();
  const existing = store.maps[stored.id];
  const saved = { ...stored, updatedAt: Date.now() };
  // Preserve a cloud-assigned share slug when the caller's copy doesn't carry one
  // (e.g. the editor's in-memory StoredMap predates the slug stamp that the sync
  // layer wrote via patchCustomMap). Dropping it here would make the sync layer
  // treat the level as slug-less and mint a NEW slug, breaking already-shared links.
  if (existing && existing.slug && !saved.slug) saved.slug = existing.slug;
  store.maps[saved.id] = saved;
  writeStore(store);
  emitMutate("save", saved);
  return saved;
}

/**
 * Merge `fields` into a stored map WITHOUT bumping `updatedAt` and WITHOUT emitting
 * a mutation. This is the "silent local write" contract (same as replaceAllLocal):
 * the sync layer uses it to stamp a cloud-assigned `slug` onto the local copy
 * without re-triggering a cloud push (which would loop) or a spurious updatedAt bump
 * (which would defeat last-write-wins merge). Returns the patched map, or null if
 * the id isn't present locally.
 */
export function patchCustomMap(id, fields) {
  const store = readStore();
  const existing = store.maps[id];
  if (!existing) return null;
  const patched = { ...existing, ...fields };
  store.maps[id] = patched;
  writeStore(store);
  return patched;
}

/** Remove a custom map by id. */
export function deleteCustomMap(id) {
  const store = readStore();
  if (store.maps[id]) {
    delete store.maps[id];
    writeStore(store);
    emitMutate("delete", id);
  }
}

/**
 * Create, persist, and return a fresh blank level: one 30-wide chunk of flat
 * ground (two solid bottom rows), the rest empty.
 */
export function createBlankMap() {
  const stored = {
    id: `${CUSTOM_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "Untitled Level",
    description: "",
    updatedAt: Date.now(),
    widthScenes: 1,
    rows: blankRows(1),
  };
  return saveCustomMap(stored);
}

// --- Export / import ----------------------------------------------------------

// A tag in the exported JSON so import can tell our files apart from arbitrary
// JSON and reject the latter with a friendly message. Bump VERSION alongside the
// stored-grid grammar if it ever changes incompatibly.
export const EXPORT_FORMAT = "sbbvibe-level";
export const EXPORT_VERSION = 1;

// Every glyph the editor/runtime understands. Anything else in an imported file
// (corruption, a future format, hand-editing) is normalized away to empty space.
const KNOWN_CHARS = new Set([".", "B", "S", "L", "l", "p", "P"]);

/**
 * A plain, portable representation of a level for sharing as a `.json` file.
 * Drops the local-only fields (id, updatedAt) — import always mints a fresh id —
 * and keeps only the level's actual content plus the format tag.
 */
export function serializeMap(stored) {
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    name: (stored && stored.name) || "Untitled Level",
    description: (stored && stored.description) || "",
    widthScenes: Math.max(1, (stored && stored.widthScenes) || 1),
    rows: Array.isArray(stored && stored.rows) ? stored.rows.slice() : blankRows(1),
  };
}

/**
 * Turn exported data (a JSON string or already-parsed object) into a fresh,
 * persisted StoredMap with a new id. Throws a user-facing Error if the input
 * isn't a recognizable level. Rows are sanitized to the known glyph set and
 * normalized to the exact width, so a hand-edited/corrupt file can't poison the
 * editor or runtime.
 */
export function importMap(data) {
  let obj = data;
  if (typeof obj === "string") {
    try {
      obj = JSON.parse(obj);
    } catch (_) {
      throw new Error("That file isn't valid JSON.");
    }
  }
  if (!obj || typeof obj !== "object") {
    throw new Error("That file isn't a Super Block Boy level.");
  }
  if (obj.format && obj.format !== EXPORT_FORMAT) {
    throw new Error("That JSON isn't a Super Block Boy level.");
  }
  const widthScenes = Math.max(1, Math.floor(Number(obj.widthScenes)) || 1);
  const rows = sanitizeRows(obj.rows, widthScenes);
  const name =
    typeof obj.name === "string" && obj.name.trim()
      ? obj.name.trim().slice(0, 48)
      : "Imported Level";
  const stored = {
    id: `${CUSTOM_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    description: typeof obj.description === "string" ? obj.description : "",
    updatedAt: Date.now(),
    widthScenes,
    rows,
  };
  return saveCustomMap(stored);
}

// Normalize arbitrary imported rows: clamp/pad to the exact grid, strip any
// unknown glyph to empty, and trim back to stored form.
function sanitizeRows(rows, widthScenes) {
  const grid = rowsToGrid(Array.isArray(rows) ? rows : [], widthScenes);
  for (const row of grid) {
    for (let x = 0; x < row.length; x++) {
      if (!KNOWN_CHARS.has(row[x])) row[x] = EMPTY;
    }
  }
  return gridToRows(grid);
}

// --- Sharing: slugs & community map refs ---------------------------------------
//
// A shared level is addressed as "<username>/<slug>". The username is the player's
// public profile name (3-20 [a-zA-Z0-9_]); the slug is their per-level public id
// (1-30 [a-zA-Z0-9_-], auto 4 hex chars, user-customizable). These mirror the DB
// grammar in supabase/migrations (profiles username CHECK + levels slug CHECK).

export const SLUG_RE = /^[a-zA-Z0-9_-]{1,30}$/;
const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/;

// Canonical public host for copyable share links (the itch embed / dev hosts can
// still RESOLVE a ?map=user/slug, but a shared link should point at the web app).
const SHARE_HOST = "https://marathon.omiigames.com/";

/** Is `slug` a valid public map slug (1-30 alphanumeric, "-" or "_", no spaces)? */
export function isValidSlug(slug) {
  return typeof slug === "string" && SLUG_RE.test(slug);
}

/** The runtime/url map id for a community level: "<username>/<slug>". */
export function communityMapId(username, slug) {
  return `${username}/${slug}`;
}

/** A copyable canonical share link for "<username>/<slug>" (literal slash). */
export function shareUrlFor(username, slug) {
  return `${SHARE_HOST}?map=${communityMapId(username, slug)}`;
}

/**
 * Parse user-pasted share input into { username, slug, version } or null. Accepts
 * a bare "username/slug", a full share URL (any host), and a percent-encoded "%2F"
 * form. An optional "@<n>" suffix selects a specific PUBLISHED version (e.g.
 * "alice/spikes@2"); without it `version` is null (the current version). Validates
 * both parts against the username/slug grammar so garbage can't navigate to a
 * broken ?map=.
 */
export function parseShareRef(input) {
  if (typeof input !== "string") return null;
  let s = input.trim();
  if (!s) return null;
  // Pull the value out of a ?map=/&map= param if present (a pasted full URL).
  const mapParam = s.match(/[?&]map=([^&#]+)/);
  if (mapParam) {
    s = mapParam[1];
  } else if (/^https?:\/\//i.test(s)) {
    return null; // a URL with no map param is not a share link
  }
  try {
    s = decodeURIComponent(s);
  } catch {
    /* not valid percent-encoding — use as-is */
  }
  s = s.trim();
  // "username/slug" with an optional "@<version>" suffix.
  const m = s.match(/^([^/]+)\/([^/@]+)(?:@(\d+))?$/);
  if (!m) return null;
  const [, username, slug, versionStr] = m;
  if (!USERNAME_RE.test(username) || !SLUG_RE.test(slug)) return null;
  const version = versionStr ? parseInt(versionStr, 10) : null;
  if (version !== null && (!Number.isInteger(version) || version < 1)) return null;
  return { username, slug, version };
}

// --- Grid helpers (shared with the editor) ------------------------------------

const EMPTY = ".";
const BLOCK = "B";

/** A full SCENE_HEIGHT x (widthScenes*30) grid of flat ground (2 solid rows). */
export function blankRows(widthScenes) {
  const cols = widthScenes * SCENE_WIDTH;
  const empty = EMPTY.repeat(cols);
  const solid = BLOCK.repeat(cols);
  const rows = [];
  for (let r = 0; r < SCENE_HEIGHT; r++) {
    rows[r] = r >= SCENE_HEIGHT - 2 ? solid : empty;
  }
  return rows;
}

/**
 * Normalize stored rows into a full SCENE_HEIGHT x (widthScenes*30) char grid
 * (array of arrays), padding missing top rows with empty and clamping/padding
 * each row to the exact width. Defensive against hand-edited/corrupt storage.
 */
export function rowsToGrid(rows, widthScenes) {
  const cols = widthScenes * SCENE_WIDTH;
  const src = Array.isArray(rows) ? rows : [];
  // Bottom-anchor: the last `src.length` rows map to the bottom of the grid.
  const grid = [];
  const topPad = Math.max(0, SCENE_HEIGHT - src.length);
  for (let r = 0; r < SCENE_HEIGHT; r++) {
    const srcRow = r >= topPad ? src[r - topPad] : "";
    const chars = (typeof srcRow === "string" ? srcRow : "").split("");
    const row = [];
    for (let x = 0; x < cols; x++) row[x] = chars[x] || EMPTY;
    grid[r] = row;
  }
  return grid;
}

/**
 * Serialize a full char grid back to stored rows (one string per row, exact
 * width). Trims fully-empty top rows for compactness, always keeping >= 1 row.
 */
export function gridToRows(grid) {
  const rows = grid.map((row) => row.join(""));
  let firstContent = rows.findIndex((row) => /[^.]/.test(row));
  if (firstContent < 0) firstContent = rows.length - 1; // all empty → keep last row
  return rows.slice(firstContent);
}

// --- Runtime map construction -------------------------------------------------

/**
 * Convert a StoredMap into a runtime designed-map object the LevelGenerator can
 * play. Slices the continuous grid into `widthScenes` scenes of exactly 30
 * columns (each a full SCENE_HEIGHT-row, bottom-anchored layout so a top-row
 * laser stays a full-height gate) and tags the map for absolute placement.
 *
 * Spawner containment is validated holistically on the continuous grid by the
 * editor (a spawner's backstop block may legitimately sit in an earlier chunk),
 * so the per-scene check is disabled here.
 *
 * @returns {{ map: Object } | { error: string }}
 */
export function buildRuntimeMap(stored) {
  try {
    if (!stored) throw new Error("no stored map");
    const widthScenes = Math.max(1, stored.widthScenes || 1);
    const grid = rowsToGrid(stored.rows, widthScenes);

    // Keep the player's spawn cell clear so nothing kills the player on frame 1.
    // The editor locks this cell, but guard legacy/imported saves defensively.
    grid[SPAWN_ROW][SPAWN_COL] = EMPTY;

    const scenes = {};
    const sequence = [];
    for (let i = 0; i < widthScenes; i++) {
      const startCol = i * SCENE_WIDTH;
      const layout = grid
        .map((row) => row.slice(startCol, startCol + SCENE_WIDTH).join(""))
        .join("\n");
      const name = `scene-${i + 1}`;
      scenes[name] = { layout, metadata: {} };
      sequence.push(name);
    }

    // The designed-map runtime places sequence[0] behind the spawn (world -15..0)
    // and starts the player at x=0 — i.e. at the END of the first scene. Prepend a
    // flat lead-in (the always-available "flat_ground") so the player instead
    // spawns at the START of the user's painted chunk 1 (true WYSIWYG), and so a
    // single-chunk course doesn't put the finish line right on the spawn.
    sequence.unshift("flat_ground");

    const map = defineDesignedMap({
      id: stored.id,
      name: stored.name || "Untitled Level",
      description: stored.description || "",
      scenes,
      sequence,
      placement: "absolute",
      requireSpawnerBackstop: false,
    });
    return { map };
  } catch (err) {
    return { error: err && err.message ? err.message : String(err) };
  }
}
